import { dirname } from 'path';
import { inspect } from 'util';

import fg from 'fast-glob';
import fs from 'fs-extra';
import latestVersion from 'latest-version';

import { createExec } from '../../../utils/exec.js';
import { log } from '../../../utils/logging.js';
import { getConsumerManifest } from '../../../utils/manifest.js';
import type { PatchFunction } from '../../lint/internalLints/upgrade/index.js';

import { Git } from '@skuba-lib/api';

export const patchInstrumentation: PatchFunction = async ({
  mode,
  packageManager,
}) => {
  const [dockerfilePaths, tsPaths] = await Promise.all([
    fg(['**/Dockerfile*'], {
      ignore: ['**/.git', '**/node_modules'],
    }),
    fg(['**/*.ts'], {
      ignore: ['**/.git', '**/node_modules'],
    }),
  ]);

  if (!dockerfilePaths.length) {
    return {
      result: 'skip',
      reason: 'no Dockerfile files found',
    };
  }

  const [dockerFiles, tsFiles] = await Promise.all([
    Promise.all(
      dockerfilePaths.map(async (filePath) => ({
        filePath,
        content: await fs.promises.readFile(filePath, 'utf-8'),
      })),
    ),
    Promise.all(
      tsPaths.map(async (filePath) => ({
        filePath,
        content: await fs.promises.readFile(filePath, 'utf-8'),
      })),
    ),
  ]);

  const hasDDTraceImport = tsFiles.some(({ content }) =>
    /from\s+['"]dd-trace['"]/.test(content),
  );
  const hasDDTraceInitImport = tsFiles.some(({ content }) =>
    /from\s+['"]dd-trace\/init['"]/.test(content),
  );
  const otelImports = tsFiles.filter(({ content }) =>
    /from\s+['"]@opentelemetry\/api['"]/.test(content),
  );

  const hasOpenTelemetryImport = otelImports.length > 0;

  if (!hasDDTraceImport && !hasDDTraceInitImport && !hasOpenTelemetryImport) {
    return {
      result: 'skip',
      reason:
        'no imports for Datadog or OpenTelemetry instrumentation found in source files',
    };
  }

  const confused =
    (hasDDTraceImport || hasDDTraceInitImport) && hasOpenTelemetryImport;

  if (confused) {
    log.warn(
      'Found imports for both Datadog and OpenTelemetry instrumentation in source files, unsure which to patch',
    );
  }

  const warning = confused
    ? 'TODO: skuba failed to determine whether to add dd-trace or OpenTelemetry flags, please choose the appropriate flags to add to your Dockerfile '
    : '';

  const commandsToAdd = [
    hasDDTraceImport ? '--import dd-trace/register.js' : '',
    hasOpenTelemetryImport
      ? '--experimental-loader @opentelemetry/instrumentation/hook.mjs'
      : '',
  ].filter(Boolean);

  const patched = dockerFiles
    .map(({ filePath, content }) => {
      if (filePath.includes('dev-deps')) {
        return null;
      }

      if (
        content.includes('dd-trace/initialize.mjs') ||
        content.includes('dd-trace/register.js') ||
        content.includes('@opentelemetry/instrumentation/hook.mjs')
      ) {
        return null;
      }

      const cmd = /^CMD\s+(.+)$/m.exec(content)?.[1];

      if (!cmd) {
        return null;
      }

      // check if CMD is in shell or exec form
      const isShellForm = !cmd.startsWith('[');

      if (isShellForm) {
        if (cmd.startsWith('node ')) {
          const patchedCmd = cmd.replace(
            'node ',
            `node ${warning}${commandsToAdd.join(' ')} `,
          );
          return { filePath, content: content.replace(cmd, patchedCmd) };
        }

        return {
          filePath,
          content: content.replace(
            cmd,
            `${warning}${commandsToAdd.join(' ')} ${cmd}`,
          ),
        };
      }

      const flags = commandsToAdd
        .map((f) => f.split(' '))
        .flat()
        .map((f) => `"${f}"`)
        .join(', ');

      if (/^\s*\[\s*"node"\s*[,\]]/.test(cmd)) {
        const patchedCmd = cmd.replace(
          /^(\s*\[\s*"node"\s*)([,\]])/,
          `$1, ${warning}${flags}$2`,
        );

        return { filePath, content: content.replace(cmd, patchedCmd) };
      }

      const patchedCmd = cmd.replace(/^\s*\[/, `[${warning}${flags}, `);
      return { filePath, content: content.replace(cmd, patchedCmd) };
    })
    .filter((patch) => patch !== null);

  if (!patched.length) {
    return {
      result: 'skip',
      reason: 'no CMD instructions found in Dockerfiles to patch',
    };
  }

  if (mode === 'lint') {
    return {
      result: 'apply',
    };
  }

  if (hasOpenTelemetryImport) {
    const packageJsons = await Promise.all(
      otelImports.map(async ({ filePath }) =>
        getConsumerManifest(dirname(filePath)),
      ),
    );

    const filteredPackageJsons = packageJsons.filter(
      (pkg) => pkg !== undefined,
    );
    await Promise.all(
      filteredPackageJsons.map(async (pkg) => {
        const { packageJson, path } = pkg;

        if (
          packageJson.dependencies?.['@opentelemetry/instrumentation'] ||
          packageJson.devDependencies?.['@opentelemetry/instrumentation']
        ) {
          return;
        }

        const fallbackVersion = '0.216.0';

        const existingVersion =
          packageJson.dependencies?.['@opentelemetry/instrumentation-http'] ??
          packageJson.dependencies?.['@opentelemetry/sdk-node'];

        const versionToUse =
          existingVersion?.startsWith('catalog:') ||
          existingVersion === undefined
            ? fallbackVersion
            : await latestVersion('@opentelemetry/instrumentation', {
                version: `<=${existingVersion}`,
              });

        packageJson.dependencies ??= {};

        packageJson.dependencies['@opentelemetry/instrumentation'] =
          versionToUse;

        await fs.promises.writeFile(
          path,
          JSON.stringify(packageJson, null, 2),
          'utf-8',
        );
      }),
    );

    const gitRoot =
      (await Git.findRoot({ dir: process.cwd() })) ?? process.cwd();

    const rootExec = createExec({ cwd: gitRoot });

    try {
      await rootExec(
        packageManager.command,
        'install',
        ...(packageManager.command === 'pnpm'
          ? ['--frozen-lockfile=false']
          : []),
        '--prefer-offline',
        '--ignore-scripts',
      );
    } catch (error) {
      log.warn('Failed to install dependencies after OpenTelemetry patch');
      log.subtle(inspect(error));
    }
  }

  await Promise.all(
    patched.map(({ filePath, content }) =>
      fs.promises.writeFile(filePath, content, 'utf-8'),
    ),
  );

  return {
    result: 'apply',
  };
};

export const tryPatchInstrumentation: PatchFunction = async (opts) => {
  try {
    return await patchInstrumentation(opts);
  } catch (err) {
    log.warn(
      'Failed to patch Datadog or OpenTelemetry instrumentation, skipping',
    );
    log.subtle(inspect(err));
    return { result: 'skip', reason: 'due to an error' };
  }
};
